import { ISubscriptionPayload } from './Subscription';
import { IDowngradeDetailsPayload } from './DowngradeDetails';
import { ICancellationDetails } from './CancellationDetails';
import { LicenseCodename } from '../main/constants/LicenseCodename';
import { DashboardLang } from '../constants/DashboardLang';
import { Notifications } from './Notifications';
import { HomeScreen } from '../constants/HomeScreen';

export interface INotificationsPayload {
    isActive: boolean;
    isTrial: boolean;
    accountDisabled?: boolean;
    lastPaymentFailed?: boolean;
    inVerification?: boolean;
    paymentRefused?: boolean;
    trialExpiresAt?: string;
    updateEmail?: string;
    nextSubscription?: string;
    downgradeSubscription?: ISubscriptionPayload;
    desiredLicense?: string;
    desiredLicensePeriod?: string;
    remainingChargeAttempts?: number;
    hasPendingMethodChange?: boolean;
    pendingDowngradeDetails?: IDowngradeDetailsPayload;
    pendingCancellationDetails?: ICancellationDetails;
    needsWelcome: boolean;
}

enum UserRoles {
    Admin = 'ROLE_ADMIN',
    User = 'ROLE_USER',
}

export interface IUserPayload {
    accountId: string;
    codename: LicenseCodename;
    email: string;
    firstName: string;
    lastName: string;
    avatar: string;
    adHoc: boolean;
    hasApi: boolean;
    userId: string;
    languageCode: DashboardLang;
    onBoarding: any;
    notifications: INotificationsPayload;
    role: string;
    accountConfigs?: IAccountConfigs;
    homeScreen: HomeScreen | null;
    isTrial: boolean;
    companyName: string;
}

export interface IAccountConfigs {
    maxFileSize: number;
}

export class User {
    private readonly _accountId: string;
    private readonly _codename: LicenseCodename;
    private readonly _email: string;
    private readonly _firstName: string;
    private readonly _lastName: string;
    private readonly _avatar: string;
    private readonly _adHoc: boolean;
    private readonly _hasApi: boolean;
    private readonly _userId: string;
    private readonly _language: DashboardLang;
    private readonly _role: string;
    private readonly _notifications: Notifications;
    private readonly _accountConfigs: IAccountConfigs | undefined;
    private readonly _homeScreen: HomeScreen | null;
    private readonly _isTrial: boolean;
    private readonly _companyName: string;

    constructor(user: IUserPayload) {
        this._accountId = user.accountId;
        this._firstName = user.firstName;
        this._lastName = user.lastName;
        this._accountConfigs = user.accountConfigs;
        this._avatar = user.avatar;
        this._adHoc = user.adHoc;
        this._hasApi = user.hasApi;
        this._email = user.email;
        this._userId = user.userId;
        this._codename = user.codename;
        this._language = user.languageCode;
        this._notifications = new Notifications(user.notifications, user.codename);
        this._role = user.role;
        this._homeScreen = user.homeScreen;
        this._isTrial = user.isTrial;
        this._companyName = user.companyName;
    }

    get accountId(): string {
        return this._accountId;
    }

    get userId(): string {
        return this._userId;
    }

    get email(): string {
        return this._email;
    }

    get firstName(): string {
        return this._firstName;
    }

    get lastName(): string {
        return this._lastName;
    }

    get avatar(): string {
        return this._avatar;
    }

    get notifications(): Notifications {
        return this._notifications;
    }

    get accountConfigs(): IAccountConfigs | undefined {
        return this._accountConfigs;
    }

    get adHoc(): boolean {
        return this._adHoc;
    }

    get hasApi(): boolean {
        return this._hasApi;
    }

    get codename(): LicenseCodename {
        return this._codename;
    }

    get language(): DashboardLang {
        return this._language;
    }

    get role(): string {
        return this._role;
    }

    get isAdmin(): boolean {
        return this._role === UserRoles.Admin;
    }

    get isPayingUser(): boolean {
        return this._notifications && !this._notifications.isTrial && !this._adHoc;
    }

    get companyName(): string {
        return this._companyName;
    }

    /* @deprecated This logic will be controlled from the backend so that there is no different concept of what a trial is in the frontend.
        This part will be modified in the next iterations. */
    public get isTrial(): boolean {
        return this.notifications && this.notifications.isTrial && !this.adHoc;
    }

    public get isTrialAccount(): boolean {
        return this._isTrial;
    }

    public get isOnlinePayment(): boolean {
        return !this.adHoc;
    }

    get homeScreen(): HomeScreen | null {
        return this._homeScreen;
    }
}
